import axios from 'axios';
import { API_URL } from './api';

/** Base path for API - avoids repeating API_URL in every call */
const base = (path: string) => `${API_URL}${path.startsWith('/') ? path : '/' + path}`;

// Attach the staff/admin JWT (localStorage `olso_token`) to every request when
// present. Purely additive: public endpoints ignore the header, so this changes
// no existing behavior — it only lets protected routes recognize the caller.
// A caller that sets its own Authorization header (e.g. customer-token flows)
// keeps it; we never overwrite an existing one.
axios.interceptors.request.use((config) => {
  try {
    const token = localStorage.getItem('olso_token');
    if (token && !config.headers?.Authorization) {
      config.headers = config.headers ?? {};
      (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }
  } catch {
    // localStorage unavailable (SSR/private mode) — send request unmodified.
  }
  return config;
});

// Axios instance with cookies enabled (needed for session cookie auth on iOS PWA)
const axiosWithCookies = axios.create({ withCredentials: true });

export { API_URL };
export const api = {
  get: <T = unknown>(path: string, config?: Parameters<typeof axios.get>[1]) =>
    axios.get<T>(base(path), config),
  post: <T = unknown>(path: string, data?: unknown, config?: Parameters<typeof axios.post>[2]) =>
    axios.post<T>(base(path), data, config),
  patch: <T = unknown>(path: string, data?: unknown, config?: Parameters<typeof axios.patch>[2]) =>
    axios.patch<T>(base(path), data, config),
  put: <T = unknown>(path: string, data?: unknown, config?: Parameters<typeof axios.put>[2]) =>
    axios.put<T>(base(path), data, config),
  delete: <T = unknown>(path: string, config?: Parameters<typeof axios.delete>[1]) =>
    axios.delete<T>(base(path), config),
};

// Session cookie endpoints — always sent with credentials
export const sessionApi = {
  create: (idToken: string) =>
    axiosWithCookies.post(base('/api/auth/customer/session'), { idToken }),
  restore: <T = unknown>() =>
    axiosWithCookies.get<T>(base('/api/auth/customer/session')),
  destroy: () =>
    axiosWithCookies.delete(base('/api/auth/customer/session')),
};
